var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var MultiFormulaConfig_1;
/* eslint-disable import/no-duplicates */
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import { FUNCTION_NAMES } from './formulaConfig';
import { FormulaValidationError, FormulaValidationInfo } from './formulaValidationInfo';
import './multiFormulaColumn'; // force import for JsonSchema
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult, ToolValidationState } from './validation/toolValidationResult';
import { isSourceValid, validateSourceWithDetail } from './validation/validateHelper';
const schema = {
    $id: 'urn:wisepipe-common-pipe:MultiFormulaConfig',
    type: 'object',
    properties: {
        multiFormulaColumns: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-common-pipe:MultiFormulaColumn' },
            uniqueItemProperties: ['column']
        }
    },
    required: ['multiFormulaColumns']
};
let MultiFormulaConfig = MultiFormulaConfig_1 = class MultiFormulaConfig extends ToolConfig {
    constructor(multiFormulaColumns, customFormats) {
        super(ToolType.MULTI_FORMULA, customFormats);
        this.multiFormulaColumns = multiFormulaColumns;
        JSON.validate(this, MultiFormulaConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return MultiFormulaConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return MultiFormulaConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return MultiFormulaConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return MultiFormulaConfig_1.outs;
    }
    static get in() {
        return MultiFormulaConfig_1.ins[0];
    }
    static get out() {
        return MultiFormulaConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        const aliases = FUNCTION_NAMES.map((name) => ({
            name,
            weight: name === 'date' ? 0.1 : undefined
        }));
        aliases.push({ name: 'leading', weight: undefined }, { name: 'trailing', weight: undefined }, { name: 'padding', weight: undefined }, { name: 'prefix', weight: undefined }, { name: 'suffix', weight: undefined });
        return new ToolConfigInfo(ToolType.FORMULA.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CALCULATE, ToolCategories.THE_BASICS, ToolCategories.COMPUTE], this.ins, this.outs, aliases, true);
    }
    applyParameters(parameters) {
        return new MultiFormulaConfig_1(this.multiFormulaColumns.map((mfc) => mfc.applyParameters(parameters)), this.customFormats);
    }
    // eslint-disable-next-line class-methods-use-this
    getBaseValues(parameters) {
        // unneeded
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        const exp = tsb.export(this.out);
        if (source) {
            let found = false;
            const newColumnFormats = new Map();
            // dependencyMap will contain the dependency of the formula column.
            // So when the formula reference previous formula column, we will use previous column's dependent columns
            const dependencyMap = new Map();
            const parameters = iConfigHelper.getParameters();
            const colsToReplace = this.multiFormulaColumns.map((a) => a.columnToReplace).filter((n) => n !== undefined);
            this.multiFormulaColumns.forEach((a) => {
                const columnFormats = new Map();
                source.forEach((col) => {
                    if (col.customFormat)
                        columnFormats.set(col.name, col.customFormat);
                });
                const aVals = a.getBaseValues(parameters);
                const result = iConfigHelper.evalSql(aVals.formula, MultiFormulaConfig_1.PRESTO_ID_CHAR, MultiFormulaConfig_1.STRING_CHAR, true, source, columnFormats);
                if (result.type !== null) {
                    if (!found) {
                        // copy all except for columns that will be replaced with formula results
                        source
                            .filter((s) => !colsToReplace.includes(s.name))
                            .forEach((s) => {
                            exp.copy(this.in, s, false, false);
                        });
                        found = true;
                    }
                    const dependents = new Set();
                    result.colArgs.forEach((columnName) => {
                        const sourceColumn = exp.findSourceColumn(this.in, columnName);
                        // referencing the column in data
                        if (sourceColumn) {
                            dependents.add(sourceColumn);
                        }
                        // referencing the previous formula
                        else {
                            const colArgs = dependencyMap.get(columnName);
                            colArgs.forEach((col) => {
                                const referenceColumn = exp.findSourceColumn(this.in, col);
                                dependents.add(referenceColumn);
                            });
                        }
                    });
                    // set current formula to dependencyMap so it can be called in future formulas
                    dependencyMap.set(parameters.getStrVal(aVals.column), Array.from(dependents).map((d) => d.name));
                    if (aVals.columnToReplace) {
                        // data type may have changed
                        exp.changeType(this.in, exp.findSourceColumn(this.in, aVals.columnToReplace), result.type, true);
                    }
                    else {
                        exp.makeNew(new Column(aVals.column, result.type), Array.from(dependents), true);
                    }
                    if (result.format) {
                        newColumnFormats.set(aVals.column, result.format.copyWithAutoGeneratedTrue());
                    }
                }
            });
            exp.initializeColumnFormats().setColumnFormats(newColumnFormats).setColumnFormats(this.customFormats);
        }
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        const parameters = iConfigHelper.getParameters();
        // check all formulas are valid with this source
        // We need to be able to reference previous formula columns therefore insert the formula columns as we loop through each formulas.
        const col = source.map((c) => c.column);
        for (const x of this.multiFormulaColumns) {
            const xVals = x.getBaseValues(parameters);
            const isValid = iConfigHelper.parseFormula(x.formula, col);
            if (!isValid)
                return ToolValidationResult.getBadMetadataResult('One or more formulas are invalid.');
            const result = iConfigHelper.evalSql(xVals.formula, MultiFormulaConfig_1.PRESTO_ID_CHAR, MultiFormulaConfig_1.STRING_CHAR, true, col);
            if (!xVals.column)
                return ToolValidationResult.getInvalidResult('One or more formulas are missing a column name.', 'Please provide a column name for each formula.');
            col.push(new Column(xVals.column, result.type));
        }
        // check there are no naming conflicts between source columns and formula columns
        const outputs = this.multiFormulaColumns.map((x) => parameters.getStrVal(x.column));
        // inputs are columns selected to be replaced (if any)
        const inputs = this.multiFormulaColumns
            .map((formula) => formula.columnToReplace)
            .filter((columnToReplace) => columnToReplace !== undefined);
        const sourceValidationResult = validateSourceWithDetail(source, new Set(inputs), outputs);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
    isValidForCalcDetail(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return new FormulaValidationInfo(ToolValidationState.BAD_METADATA, 'The source is invalid.', FormulaValidationError.INVALID_SOURCE);
        }
        // check all formulas are valid with this source
        // We need to be able to reference previous formula columns therefore insert the formula columns as we loop through each formulas.
        const parameters = iConfigHelper.getParameters();
        const col = source.map((c) => c.column);
        for (const x of this.multiFormulaColumns) {
            const xVals = x.getBaseValues(parameters);
            const isValid = iConfigHelper.parseFormulaDetail(xVals.formula, col);
            if (isValid.validationState !== ToolValidationState.VALID) {
                return isValid;
            }
            const result = iConfigHelper.evalSql(xVals.formula, MultiFormulaConfig_1.PRESTO_ID_CHAR, MultiFormulaConfig_1.STRING_CHAR, true, col);
            col.push(new Column(xVals.column, result.type));
        }
        // check there are no naming conflicts between source columns and formula columns
        const outputs = this.multiFormulaColumns.map((x) => parameters.getStrVal(x.column));
        if (!isSourceValid(source, new Set(), outputs)) {
            return new FormulaValidationInfo(ToolValidationState.BAD_METADATA, 'The source is invalid.', FormulaValidationError.INVALID_SOURCE);
        }
        return new FormulaValidationInfo(ToolValidationState.VALID);
    }
};
MultiFormulaConfig.PRESTO_ID_CHAR = '"';
MultiFormulaConfig.SPARK_ID_CHAR = '`';
MultiFormulaConfig.STRING_CHAR = "'";
MultiFormulaConfig = MultiFormulaConfig_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.MULTI_FORMULA)
], MultiFormulaConfig);
export { MultiFormulaConfig };
//# sourceMappingURL=multiFormulaConfig.js.map